import React from 'react';
import ReactECharts from 'echarts-for-react';
import {
  INTERNAL_SE_SOFT_BLUE_COLORS,
  buildChartTheme,
  truncateChartLabel,
} from './chartTheme';
import useLaptopCompact from './useLaptopCompact';
import ChartEmptyState from './ChartEmptyState';
import DonutChartFrame from './DonutChartFrame';

const IngestionDonut = ({
  data,
  renderZeroWhenEmpty = false,
  emptyStateTitle = 'No activity for this period',
  emptyStateSubtitle = 'Try changing the time filter.',
  emptyStateCompact = false,
}) => {
  const isLaptopCompact = useLaptopCompact();
  const theme = buildChartTheme(isLaptopCompact);

  const hasTypes = Array.isArray(data?.types) && data.types.length > 0;
  if (!hasTypes && !renderZeroWhenEmpty) {
    return (
      <ChartEmptyState
        title={emptyStateTitle}
        subtitle={emptyStateSubtitle}
        compact={emptyStateCompact}
      />
    );
  }

  const normalizedData = hasTypes
    ? data
    : {
        total: 0,
        types: [
          { name: 'Instant Run', percent: 0, value: 0 },
          { name: 'One-time Schedule', percent: 0, value: 0 },
          { name: 'Recurring Schedule', percent: 0, value: 0 },
          { name: 'Unknown', percent: 0, value: 0 },
        ],
      };

  const chartData = normalizedData.types.map((t, i) => ({
    name: t.name,
    value: t.percent ?? t.value,
    itemStyle: { color: INTERNAL_SE_SOFT_BLUE_COLORS[i % INTERNAL_SE_SOFT_BLUE_COLORS.length] },
  }));

  const donutCenter = ['50%', '42%'];
  const chartHeight = isLaptopCompact ? 206 : 220;

  const options = {
    animation: false,
    tooltip: {
      ...theme.tooltipBase,
      trigger: 'item',
      formatter: '{b}: {c}%',
    },
    legend: {
      ...theme.legendBase,
      bottom: 0,
      left: 'center',
      orient: 'horizontal',
      itemWidth: 10,
      itemHeight: 10,
      textStyle: { fontSize: theme.fs(11), color: '#374151' },
      formatter: (name) => {
        const item = normalizedData.types.find((t) => t.name === name);
        if (!item) return name;
        const pct = item.percent ?? item.value;
        const short = name.replace(' Schedule', '').replace('Instant Run', 'Instant');
        return `${truncateChartLabel(short, 18)} ${pct}%`;
      },
    },
    series: [{
      type: 'pie',
      radius: ['46%', '68%'],
      center: donutCenter,
      label: { show: false },
      labelLine: { show: false },
      data: chartData,
    }],
  };

  return (
    <DonutChartFrame
      center={donutCenter}
      centerLabel={String(normalizedData.total)}
      centerStyle={{ fontSize: theme.fs(17) }}
      height={`${chartHeight}px`}
    >
      <ReactECharts
        option={options}
        style={{ height: `${chartHeight}px`, width: '100%' }}
        opts={{ renderer: 'canvas' }}
      />
    </DonutChartFrame>
  );
};

const RegionalIngestionOverview = ({
  data,
  renderZeroWhenEmpty = false,
  emptyStateTitle = 'No activity for this period',
  emptyStateSubtitle = 'Try changing the time filter.',
  emptyStateCompact = false,
}) => {
  const hasRegionData = !!(data && Object.keys(data).length > 0);
  if (!hasRegionData && !renderZeroWhenEmpty) {
    return (
      <ChartEmptyState
        title={emptyStateTitle}
        subtitle={emptyStateSubtitle}
        compact={emptyStateCompact}
      />
    );
  }

  const normalizedData = hasRegionData
    ? data
    : {
        APJ: { total: 0, types: [] },
        EMEA: { total: 0, types: [] },
        NA: { total: 0, types: [] },
        LATAM: { total: 0, types: [] },
      };

  return (
    <div className="row g-3">
      {Object.keys(normalizedData).map((region) => (
        <div className="col-lg-3 col-md-6 col-12" key={region}>
          <div className="lead-dash-ingestion-card">
            <h4 className="lead-dash-ingestion-title">{region}</h4>
            <IngestionDonut
              data={normalizedData[region]}
              renderZeroWhenEmpty={renderZeroWhenEmpty}
              emptyStateTitle={emptyStateTitle}
              emptyStateSubtitle={emptyStateSubtitle}
              emptyStateCompact
            />
          </div>
        </div>
      ))}
    </div>
  );
};

export default RegionalIngestionOverview;
